import { IPoint2d } from '@/editor/math/IPoint2d';
import { IOriginModifyWallInfo } from '@/editor/types';
import { globalStores } from '../..';
import { getFootRatio, getPointFromRatio, getShapeSeg } from '@/editor/math/shape/other';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { getPointAndClosedRelation } from '@/editor/math/shape/path';
import { EPointAndClosedRelation, ERelationToSeg, IShapePoint2d, IShapeSeg2d } from '@/editor/types/shape';
import { getPathFromEdges } from '@/editor/utils/edge';
import { segRelations } from '@/editor/math/shape/relation';
import { intersectCommonShape } from '@/editor/math/shape/intesection';

interface IModifyWallData {
  start: IPoint2d;
  end: IPoint2d;
  center?: IPoint2d;
  clockwise?: boolean;
  relateRoomId: string;
  seg: IShapeSeg2d;
}

interface IRoomCheckInfo {
  roomPath: IShapePoint2d[];
  holePaths: IShapePoint2d[][];
  boundarySegs: IShapeSeg2d[];
}

function hasSegRelation(relation: ERelationToSeg | ERelationToSeg[], target: ERelationToSeg) {
  return Array.isArray(relation) ? relation.includes(target) : relation === target;
}

function buildSegsFromPath(path: IShapePoint2d[]) {
  const segs: IShapeSeg2d[] = [];
  if (path.length < 2) return segs;

  for (let i = 0; i < path.length; i++) {
    const start = path[i];
    const end = path[(i + 1) % path.length];
    segs.push(getShapeSeg({ start, end, center: start.center, clockwise: start.clockwise }));
  }

  return segs;
}

function getShapeCheckPoints(seg: IShapeSeg2d, boundarySegs: IShapeSeg2d[]) {
  const ratios = [0, 1];

  for (const boundarySeg of boundarySegs) {
    const intersectPoints = intersectCommonShape(seg, boundarySeg);
    if (!intersectPoints) continue;

    for (const point of intersectPoints) {
      ratios.push(Math.min(1, Math.max(0, getFootRatio(seg, point))));
    }
  }

  const sortedRatios = ratios
    .sort((a, b) => a - b)
    .filter((ratio, index, array) => index === 0 || Math.abs(ratio - array[index - 1]) > 1e-6);
  const checkPoints = sortedRatios.map((ratio) => getPointFromRatio(seg, ratio));

  for (let i = 0; i < sortedRatios.length - 1; i++) {
    if (sortedRatios[i + 1] - sortedRatios[i] <= 1e-6) continue;
    checkPoints.push(getPointFromRatio(seg, (sortedRatios[i] + sortedRatios[i + 1]) / 2));
  }

  if (seg instanceof ArcSeg2d) {
    checkPoints.push(...seg.getPoints());
  }

  return checkPoints;
}

function getRelationIllegalReason(mainSeg: IShapeSeg2d, otherSeg: IShapeSeg2d) {
  const relation = segRelations(mainSeg, otherSeg);

  if (
    hasSegRelation(relation, ERelationToSeg.Overlap) ||
    hasSegRelation(relation, ERelationToSeg.Same) ||
    hasSegRelation(relation, ERelationToSeg.Opposite)
  ) {
    return '重叠';
  }

  if (hasSegRelation(relation, ERelationToSeg.IntersectInternal)) {
    return '相交';
  }

  return '';
}

function checkWallInRoom(data: IModifyWallData, roomInfo: IRoomCheckInfo) {
  const { seg, relateRoomId } = data;
  const checkPoints = getShapeCheckPoints(seg, roomInfo.boundarySegs);

  if (
    checkPoints.some((point) => getPointAndClosedRelation(point, roomInfo.roomPath) === EPointAndClosedRelation.Outside)
  ) {
    return {
      isLegal: false,
      reason: `墙体 ${seg.start.x},${seg.start.y} -> ${seg.end.x},${seg.end.y} 不在房间 ${relateRoomId} 内部`
    };
  }

  if (
    roomInfo.holePaths.some((path) =>
      checkPoints.some((point) => getPointAndClosedRelation(point, path) === EPointAndClosedRelation.Inside)
    )
  ) {
    return {
      isLegal: false,
      reason: `墙体 ${seg.start.x},${seg.start.y} -> ${seg.end.x},${seg.end.y} 在房间 ${relateRoomId} 洞内`
    };
  }

  for (const boundarySeg of roomInfo.boundarySegs) {
    const illegalReason = getRelationIllegalReason(seg, boundarySeg);
    if (illegalReason) {
      return {
        isLegal: false,
        reason: `墙体 ${seg.start.x},${seg.start.y} -> ${seg.end.x},${seg.end.y} 与房间 ${relateRoomId} 边界${illegalReason}`
      };
    }
  }

  return {
    isLegal: true,
    reason: ''
  };
}

// 修改的墙体是否合法
export function isModifyWallsLegal({ modifyWallInfos }: { modifyWallInfos: IOriginModifyWallInfo[] }): {
  isLegal: boolean;
  reason: string;
} {
  const modifyWallDatas: IModifyWallData[] = [];
  const relativeRoomIds = new Set<string>();
  const filterWallIds = new Set<string>();
  for (const info of modifyWallInfos) {
    const wd = globalStores.separateWallStore.getSepData(info.wallId, false);
    if (!wd)
      return {
        isLegal: false,
        reason: `未找到墙体 ${info.wallId}`
      };

    relativeRoomIds.add(wd.relateRoomId);
    filterWallIds.add(info.wallId);
    const start = info.updateBaseData.start || wd.start;
    const end = info.updateBaseData.end || wd.end;
    const center = Object.prototype.hasOwnProperty.call(info.updateBaseData, 'center')
      ? info.updateBaseData.center
      : wd.center;
    const clockwise = Object.prototype.hasOwnProperty.call(info.updateBaseData, 'clockwise')
      ? info.updateBaseData.clockwise
      : wd.clockwise;
    const seg = getShapeSeg({ start, end, center, clockwise });
    modifyWallDatas.push({
      start,
      end,
      center,
      clockwise,
      relateRoomId: wd.relateRoomId,
      seg
      // thickness: info.updateBaseData.thickness || wd.thickness
    });
  }

  // 获取房间多边形
  const roomCheckInfos = new Map<string, IRoomCheckInfo>();
  const roomOtherSegs = new Map<string, IShapeSeg2d[]>();
  for (const roomId of relativeRoomIds) {
    roomOtherSegs.set(roomId, []);
    const roomData = globalStores.roomStore.getRoomData(roomId);
    if (!roomData)
      return {
        isLegal: false,
        reason: `未找到房间 ${roomId}`
      };
    const roomPath = getPathFromEdges(roomData.pathInfos);
    const holePaths = roomData.holeInfos.map((holes) => getPathFromEdges(holes));
    roomCheckInfos.set(roomId, {
      roomPath,
      holePaths,
      boundarySegs: [roomPath, ...holePaths].flatMap(buildSegsFromPath)
    });
  }

  // 获取房间其他线段
  globalStores.separateWallStore
    .getSepDatas({
      onlyNotBase: true,
      roomIds: Array.from(relativeRoomIds)
    })
    .forEach((swd) => {
      if (filterWallIds.has(swd.id)) return;
      const otherSegs = roomOtherSegs.get(swd.relateRoomId);
      if (!otherSegs) return;
      otherSegs.push(getShapeSeg(swd));
    });

  // 计算是否有相交/重叠
  for (let i = 0; i < modifyWallDatas.length; i++) {
    const wallData = modifyWallDatas[i];
    const { seg, relateRoomId } = wallData;
    const roomInfo = roomCheckInfos.get(relateRoomId);
    const roomOtherSegList = roomOtherSegs.get(relateRoomId);
    if (!roomInfo || !roomOtherSegList) {
      return {
        isLegal: false,
        reason: `未找到房间 ${relateRoomId}`
      };
    }

    // 判断墙体是否在房间内部
    const roomCheckResult = checkWallInRoom(wallData, roomInfo);
    if (!roomCheckResult.isLegal) return roomCheckResult;

    const intersectSegs = modifyWallDatas
      .slice(i + 1)
      .filter((data) => data.relateRoomId === relateRoomId)
      .map((data) => data.seg)
      .concat(roomOtherSegList);

    // 判断墙体是否与其他墙体相交/重叠
    for (const otherSeg of intersectSegs) {
      const illegalReason = getRelationIllegalReason(seg, otherSeg);
      if (illegalReason) {
        return {
          isLegal: false,
          reason: `墙体 ${seg.start.x},${seg.start.y} -> ${seg.end.x},${seg.end.y} 与其他墙体${illegalReason}`
        };
      }
    }
  }

  return {
    isLegal: true,
    reason: ''
  };
}
